import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import SubscriptionPlans from '../../components/SubscriptionPlans';
import { hostApiClient } from '../../hostApiClient';
import { OrganizationType } from '../../models/Organization';
import {
  OrganizationSubscriptionStatus,
  SubscriptionPlanSummary,
  SubscriptionStatus,
} from '../../models/Subscription';
import { authService } from '../../services/AuthService';
import { getCachedProfileDisplay } from '../../services/profileDisplayCache';
import { userApiClient } from '../../userApiClient';
import {
  beginSubscriptionOnboardingSession,
  buildSubscriptionOnboardingPath,
  isSubscriptionOnboardingActive,
  readOnboardingDisplayName,
} from '../../utils/subscriptionOnboarding';

const OPEN_SUBSCRIPTION_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.PAUSED,
]);

/**
 * Public pricing catalogue for logged-out visitors (and anyone browsing plans).
 * Plan CTAs route into signup / onboarding / host billing — no Stripe from this page.
 */
const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState<string | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscriptionStatus | null>(null);
  const [hostBillingLoaded, setHostBillingLoaded] = useState(false);

  const isHost = Boolean(
    authService.getAccessToken() && authService.getRoles().includes('HOST'),
  );

  useEffect(() => {
    if (!isHost) {
      setOrganizationType(null);
      setSubscription(null);
      setHostBillingLoaded(true);
      return;
    }

    let cancelled = false;
    const loadHostBilling = async () => {
      try {
        const [org, status] = await Promise.all([
          hostApiClient.getOrganization(),
          hostApiClient.getSubscription(),
        ]);
        if (cancelled) return;
        setOrganizationType(org.organizationType ?? null);
        setSubscription(status);
      } catch (err) {
        console.error('Failed to load host billing for pricing page:', err);
        if (!cancelled) {
          setOrganizationType(null);
          setSubscription(null);
        }
      } finally {
        if (!cancelled) {
          setHostBillingLoaded(true);
        }
      }
    };

    setHostBillingLoaded(false);
    void loadHostBilling();
    return () => {
      cancelled = true;
    };
  }, [isHost]);

  const planCode = subscription?.planCode ?? null;
  const status = subscription?.status ?? null;
  const hasOpenSubscription = Boolean(
    planCode && status && OPEN_SUBSCRIPTION_STATUSES.has(status),
  );

  const startWithPlan = useCallback(
    async (plan: SubscriptionPlanSummary) => {
      setActionError(null);
      const token = authService.getAccessToken();
      if (!token) {
        navigate('/login', {
          state: {
            authMode: 'signup',
            redirectUrl: buildSubscriptionOnboardingPath(),
          },
        });
        return;
      }

      const roles = authService.getRoles();
      if (roles.includes('HOST')) {
        if (hasOpenSubscription) {
          const isCurrent = plan.code === planCode;
          navigate(isCurrent ? '/host/subscription' : '/host/subscription/change');
          return;
        }
        navigate('/host/subscription');
        return;
      }

      if (isSubscriptionOnboardingActive() && readOnboardingDisplayName()) {
        navigate(buildSubscriptionOnboardingPath());
        return;
      }

      // Existing musician/tutor: skip complete-profile and prefill onboarding from profile.
      if (roles.includes('USER') || roles.includes('TUTOR')) {
        try {
          const cachedName = getCachedProfileDisplay()?.name?.trim();
          const displayName = cachedName || (await userApiClient.getProfile()).name?.trim();
          if (
            displayName &&
            beginSubscriptionOnboardingSession({
              accessToken: token,
              displayName,
            })
          ) {
            navigate(buildSubscriptionOnboardingPath());
            return;
          }
        } catch (err) {
          console.error('Failed to load profile for subscription onboarding:', err);
          setActionError(
            t(
              'subscription.pricing.profileLoadFailed',
              'Could not load your profile. Please try again.',
            ),
          );
          return;
        }
      }

      navigate('/complete-profile');
    },
    [hasOpenSubscription, navigate, planCode, t],
  );

  const pageTitle = t('subscription.pricing.pageTitle', 'Pricing');
  const pageDescription = t(
    'subscription.pricing.pageDescription',
    'Compare Band Space plans for rehearsal studios, music schools, and tutors. Start with a free trial.',
  );

  const headerChip = useMemo(() => {
    if (hasOpenSubscription) {
      return {
        color: 'info' as const,
        label: t('subscription.pricing.yourPlanBadge', 'Your plan'),
      };
    }
    return {
      color: 'success' as const,
      label: t('subscription.onboarding.trialBadge', 'Free Trial'),
    };
  }, [hasOpenSubscription, t]);

  const pageSubtitle = useMemo(() => {
    if (hasOpenSubscription) {
      return t(
        'subscription.pricing.subscribedSubtitle',
        'Your current plan is marked below. Switch plans or manage billing from Plan and Billing.',
      );
    }
    return t(
      'subscription.pricing.pageSubtitle',
      'Plans for rehearsal studios, music schools, and individual tutors. Full access for 30 days — you will not be charged until the trial ends.',
    );
  }, [hasOpenSubscription, t]);

  return (
    <>
      <SEO title={pageTitle} description={pageDescription} url="/pricing" />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, pb: { xs: 10, md: 12 } }}>
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: '1.75rem', sm: '2.25rem' } }}
              >
                {pageTitle}
              </Typography>
              <Chip
                color={headerChip.color}
                size="small"
                label={headerChip.label}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            {hasOpenSubscription && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/host/subscription')}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              >
                {t('subscription.pageTitle', 'Plan and Billing')}
              </Button>
            )}
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
            {pageSubtitle}
          </Typography>
        </Stack>

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Box>
          {isHost && !hostBillingLoaded ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
              <CircularProgress />
            </Box>
          ) : (
            <SubscriptionPlans
              mode="preview"
              hideHeader
              useHostCatalogue={isHost}
              organizationType={organizationType}
              currentPlanCode={planCode}
              currentStatus={status}
              onPlanInterest={(plan) => {
                void startWithPlan(plan);
              }}
            />
          )}
        </Box>
      </Container>
    </>
  );
};

export default PricingPage;
